import { useState, useEffect, useRef, useCallback, lazy, Suspense, memo } from 'react';
import { DogSchool } from '@/data/dogSchools';
import { useLanguage } from '@/contexts/LanguageContext';
import { Skeleton } from '@/components/ui/skeleton';
import { DogSchoolCard } from '@/components/DogSchoolCard';
import { SortingOptions, SortOption, SortDirection } from '@/components/SortingOptions';
import { 
  sortSchoolsByReviewCount, 
  sortSchoolsByRating, 
  sortSchoolsAlphabetically, 
  sortSchoolsByProximity,
  sortSchoolsByProximityToCoordinates,
  sortSchoolsByDistance
} from '@/services/utils/sortUtils';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { generateSchoolId } from '@/utils/idGenerator';

// Lazy-loaded schema component to reduce initial bundle size
const SchemaGenerator = lazy(() => import('./SchemaGenerator'));

interface DogSchoolsListProps {
  schools: DogSchool[];
  loading?: boolean;
  userPostalCode?: string;
  onCountUpdate?: (count: number) => void;
  isPaginated?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

// Create a memoized card component for better performance
const MemoizedDogSchoolCard = memo(({ school, distance }: { school: DogSchool; distance?: number }) => {
  return <DogSchoolCard school={school} distance={distance} />;
});

const DogSchoolsListComponent = ({ 
  schools, 
  loading = false, 
  userPostalCode = '',
  onCountUpdate,
  isPaginated = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore
}: DogSchoolsListProps) => {
  const { t } = useLanguage();
  const [sortedSchools, setSortedSchools] = useState<DogSchool[]>(schools);
  const [sortOption, setSortOption] = useState<SortOption>('proximity');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  // Notify parent component about the school count whenever it changes
  useEffect(() => {
    if (onCountUpdate && !loading) {
      onCountUpdate(schools.length);
    }
  }, [schools.length, onCountUpdate, loading]);
  
  // Optimierte Schema-Daten-Generierung - nur bei sichtbaren Schulen
  const [shouldLoadSchema, setShouldLoadSchema] = useState(false);
  
  useEffect(() => {
    // Verzögere das Schema-Laden, bis die Hauptinhalte geladen sind
    if (schools.length > 0 && !loading) {
      const timer = setTimeout(() => {
        setShouldLoadSchema(true);
      }, 1000); // 1 Sekunde Verzögern für bessere Hauptladezeit
      
      return () => clearTimeout(timer);
    }
  }, [schools.length, loading]);

  // Update sorted schools when schools prop changes
  useEffect(() => {
    if (schools.length > 0) {
      handleSort(sortOption, sortDirection);
    } else {
      setSortedSchools([]);
    }
  }, [schools]);

  const handleSort = useCallback((option: SortOption, direction: SortDirection) => {
    console.log(`Sorting schools by ${option} in ${direction} order`);
    let sorted: DogSchool[] = [];
    
    switch (option) {
      case 'reviewCount':
        sorted = sortSchoolsByReviewCount(schools);
        if (direction === 'asc') sorted.reverse();
        break;
      case 'rating':
        sorted = sortSchoolsByRating(schools, direction === 'asc');
        break;
      case 'alphabetical':
        sorted = sortSchoolsAlphabetically(schools, direction === 'asc');
        break;
      case 'distance':
        // Sort by the distance property that was added during filtering
        sorted = sortSchoolsByDistance(schools, direction === 'asc');
        break;
      case 'proximity':
        // Check if schools have distance property
        const hasDistance = schools.some(school => (school as any).distance !== undefined);
        if (hasDistance) {
          console.log("Schools have distance property, sorting by actual distance");
          // Sort by actual calculated distance (reusing the distance sorting function)
          sorted = sortSchoolsByDistance(schools, direction === 'asc');
        } else if (userPostalCode) {
          sorted = sortSchoolsByProximity(schools, userPostalCode, direction === 'asc');
        } else {
          // Fallback to review count if no postal code
          sorted = sortSchoolsByReviewCount(schools);
          if (direction === 'asc') sorted.reverse();
        }
        break;
      default:
        sorted = [...schools];
    }
    
    setSortOption(option);
    setSortDirection(direction);
    setSortedSchools(sorted);
    console.log(`Sorted ${sorted.length} schools`);
  }, [schools, userPostalCode]);
  
  if (loading) {
    return (
      <div className="space-y-8">
        {[1, 2, 3].map((index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-4">
            <div className="flex flex-col gap-4">
              <Skeleton className="h-[250px] w-full rounded-md" />
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (sortedSchools.length === 0) {
    return (
      <div className="text-center p-8 bg-white rounded-lg shadow-md">
        <p className="text-lg text-gray-600">{t('noSchoolsFound')}</p>
        {userPostalCode && (
          <p className="text-sm text-gray-500 mt-2">
            {t('tryDifferentPostalCode')}
          </p>
        )}
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
      <SortingOptions 
        onSortChange={handleSort} 
        showProximityOption={true}
        defaultOption={sortOption}
        defaultDirection={sortDirection}
      />
      
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="space-y-10">
        {sortedSchools.map((school) => {
          const cityId = generateSchoolId(school);
          return (
            <motion.div 
              key={school.id} 
              id={cityId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <MemoizedDogSchoolCard 
                school={school} 
                distance={(school as any).distance} 
              />
            </motion.div>
          );
        })}
        
        {/* Infinite scroll loading indicator - always show for all pages */}
        <div ref={loadMoreRef} className="flex justify-center py-4">
          {isLoadingMore ? (
            <Button disabled variant="outline" className="gap-2">
              <Loader2 size={16} className="animate-spin" />
              {t('paginationLoadingMore')}
            </Button>
          ) : hasMore ? (
            <Button onClick={onLoadMore} variant="outline">
              {t('paginationLoadMore')}
            </Button>
          ) : (
            sortedSchools.length > 10 && (
              <p className="text-gray-500 text-sm">{t('paginationAllSchoolsLoaded')}</p>
            )
          )}
        </div>
      </motion.div>
      
      {/* Lazy-loaded Schema Data - wird erst nach dem Hauptinhalt geladen */}
      {shouldLoadSchema && (
        <Suspense fallback={null}>
          <SchemaGenerator schools={sortedSchools.slice(0, 10)} />
        </Suspense>
      )}
    </div>
  );
};

export const DogSchoolsList = memo(DogSchoolsListComponent);
